import DateService from '@services/DateService'
import { MeteoConditions } from '../../models/MeteoConditions'
import { MeteoConditionsDTO as ServiceMeteoConditions } from '../../models/api/MeteoConditionsDTO'
import { request } from '../request'
import type { SimulationSendDTO } from '@models/api/simulation/SimulationSendDTO'
import { Dayjs } from 'dayjs'
import { SimulationError } from '@models/api/simulation/SimulationError'
import { AxiosResponse } from 'axios'
import { SimulationReportItem } from '@models/api/simulation/SimulationReportItem'
import { SimulationWindResponse } from '@models/api/simulation/SimulationWindResponse'
import { VexizaGeojson } from '@models/api/VexizaGeojson'
import { Model } from '@models/Model'
import Constants from '@helpers/Constants'
import { FuelModelHumidityDTO } from '@models/api/simulation/FuelModelHumidityDTO'
import { SimulationListItemDTO } from '@models/api/simulation/SimulationListItemDTO'
import { SimulationDTO } from '@models/api/simulation/SimulationDTO'
import { SimulationListItem } from '@models/simulation/SimulationListItem'
import { SimulatorLayerFeatureProperties } from '@models/api/SimulatorLayerFeatureProperties'
import { getMeteoMock } from 'src/__tests__/mocks/Simulation.mock'

const BASE_URL = 'simulation'

export const simulation = {
  getSimulations: () => {
    return request.get<SimulationListItemDTO[]>(
      `${BASE_URL}/allSimulationsByDate`
    )
  },

  allSimulations: () => {
    return request.get<SimulationListItemDTO[]>(`${BASE_URL}/allSimulations`)
  },

  getSimulation: (idSimulation: string) => {
    return request.get(`${BASE_URL}/getSimulation/${idSimulation}`)
  },

  getSimulationData: (idSimulation: SimulationListItemDTO['ID_SIMULATION']) => {
    return request.get<SimulationDTO>(
      `${BASE_URL}/getSimulationData/${idSimulation}`
    )
  },

  addSimulation: (simulationData: SimulationSendDTO) => {
    return request.post('simulation/simulate', simulationData)
  },

  removeSimulation: (idSimulation: string) => {
    return request.delete(`${BASE_URL}/removeSimulation/${idSimulation}`)
  },

  getSimulationMeteoModel: (
    lat: string | number,
    long: string | number,
    hoursDuration: string | number,
    date: Dayjs
  ): Promise<MeteoConditions[]> => {
    return request
      .get(`${BASE_URL}/getMeteo`, {
        params: {
          lat: lat,
          long: long,
          hoursDuration: hoursDuration,
          date: DateService.formatDate(date, 'YYYY_MM_DD_HH_mm')
        }
      })
      .then((response) =>
        response.data
          .filter((serverMeteo: ServiceMeteoConditions) => !serverMeteo.IGNORE)
          .map((meteo: ServiceMeteoConditions) => new MeteoConditions(meteo))
      )
      .catch((error) => {
        console.error('The simulation failed on getSimulationMeteoModel', error)
        return getMeteoMock(hoursDuration as number)
          .filter((serverMeteo: ServiceMeteoConditions) => !serverMeteo.IGNORE)
          .map((meteo: ServiceMeteoConditions) => new MeteoConditions(meteo))
      })
  },

  getFlameLength: async (
    idSimulation: string
  ): Promise<VexizaGeojson | SimulationError> => {
    return (
      await request.get(`${BASE_URL}/getFlameLength`, {
        params: {
          idSimulation: idSimulation
        }
      })
    ).data
  },

  getCrownFire: async (idSimulation: string) => {
    return (
      await request.get<VexizaGeojson | SimulationError>(
        `${BASE_URL}/getCrownFire`,
        {
          params: {
            idSimulation
          }
        }
      )
    ).data
  },

  getIntensity: async (idSimulation: string) => {
    return (
      await request.get<VexizaGeojson | SimulationError>(
        `${BASE_URL}/getIntensity`,
        {
          params: {
            idSimulation: idSimulation
          }
        }
      )
    ).data
  },

  getSpreadRate: async (idSimulation: string) => {
    return (
      await request.get<VexizaGeojson | SimulationError>(
        `${BASE_URL}/getSpreadRate`,
        {
          params: {
            idSimulation: idSimulation
          }
        }
      )
    ).data
  },

  getSimulationWinds: async (idSimulation: string, date: Dayjs) => {
    return (
      await request.get<SimulationWindResponse | SimulationError>(
        `${BASE_URL}/devolverJsonWindNinja`,
        {
          params: {
            idSimulation: idSimulation,
            fecha: date.utc().format('MM-DD-YYYY_HH00')
          }
        }
      )
    ).data
  },

  getSimulationReport: (simulationId: string) => {
    return request.get<SimulationReportItem[]>(
      `${BASE_URL}/getReport?id=${simulationId}`
    )
  },

  downloadSimulation: (simulationId: string) => {
    return request
      .get(`${BASE_URL}/exportSimulation`, {
        params: {
          id: simulationId
        },
        responseType: 'blob'
      })
      .then((response) => {
        return window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/zip' })
        )
      })
  },

  downloadKMZ: (simulationId: string) => {
    return request.get(`${BASE_URL}/kml`, {
      params: {
        id: simulationId
      },
      responseType: 'blob'
    })
  },

  getSimulationPDF: (simulationId: SimulationListItem['id']) => {
    return request.get(`${BASE_URL}/getSimulationPDF`, {
      params: {
        idSimulation: simulationId
      },
      responseType: 'blob'
    })
  },

  getReportWFAUrl: async (lat: number, lon: number, idModel: Model['id']) => {
    return `${Constants.apiURL}/${`${BASE_URL}/generateReportWFA?lat=${lat}&lon=${lon}&model=${idModel}`}`
  },

  getModelFuel: async () => {
    return request.get<FuelModelHumidityDTO[]>(`${BASE_URL}/getModelFuel`)
  },

  postModelFuel: async (modelFuel: FuelModelHumidityDTO[]) => {
    return request.post(`${BASE_URL}/postModelFuel`, modelFuel)
  },

  reportIGP: async (idSimulation: SimulationListItemDTO['ID_SIMULATION']) => {
    return request.get(`${BASE_URL}/reportIgp`, {
      params: {
        idSimulation: idSimulation
      },
      responseType: 'blob'
    })
  },

  reportIGPPost: async (
    idSimulation: SimulationListItemDTO['ID_SIMULATION'],
    fileVariable: File
  ) => {
    const formData = new FormData()
    const blobFile = new Blob([fileVariable], { type: fileVariable.type })
    formData.append('file', blobFile)
    return request.post(`${BASE_URL}/reportIgp`, formData, {
      params: { idSimulation: idSimulation }
    })
  }
}
